/*
 * Copyright (c) 2022, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment, useCallback, useEffect, useState, useMemo, useRef} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    normalizeSetBundleProduct,
    getUpdateBundleChildArray
} from '@salesforce/retail-react-app/app/utils/product-utils'
import Seo from '../../components/seo'

// Components
import {
    Box,
    Stack,
    useDisclosure,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'

import {
    useProduct,
    useProducts,
    useCategory,
    useShopperCustomersMutation,
    useShopperBasketsMutation,
    useCustomerId,
    useShopperBasketsMutationHelper
} from '@salesforce/commerce-sdk-react'
import loadable from '@loadable/component'
import {Skeleton} from '@salesforce/retail-react-app/app/components/shared/ui'
// Lazy load below-fold components for better performance
// Reviews - client-side only with fallback (matches live site behavior)
const PDPReviews = loadable(() => import('../../components/pdp-reviews'), {
    fallback: <Skeleton height="200px" width="100%" />
})

// Hooks
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useVariant, useDerivedProduct} from '@salesforce/retail-react-app/app/hooks'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {useOptimizelyPage} from '../../hooks/use-optimizely-page'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {useHeaderContext} from '../../contexts/header-context'
import {useBrand} from '../../contexts/brand-context'
import {useEinsteinViewProduct} from '../../hooks/use-einstein-custom'

// MRT multi site
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue, usesSfccShopTheLook} from '../../utils/site-config'
import {setCacheControl} from '../../utils/cache-control'

import {getResponsiveImageAttributes} from '@salesforce/retail-react-app/app/utils/responsive-image'

// Project Components
import ProductView from '../../components/product-view'
import {IMAGE_WIDTHS} from '../../components/image-gallery'
import ProductSetView from './partials/product-set-view'
import InformationAccordion from './partials/information-accordion'
import FindInStoreModal from '../../components/find-in-store'
import LongDescriptionModal from '../../components/long-description-modal'
import {renderLayoutsSwitch} from '../../components/optimizely/utils/contentRenderer'
import {GDIDynamicContentProvider} from '../../components/optimizely/gdi/utils/gdi-dynamic-content-context'
import {extractPDPAreaBlocks} from './utils/extract-pdp-area-blocks'

import logger from '@salesforce/retail-react-app/app/utils/logger-instance'
import ProductLoadError from '../../components/product-load-error'

// Error page
import PageNotFound from '../page-not-found'

// constant
import {
    API_ERROR_MESSAGE,
    MAX_CACHE_AGE,
    STALE_WHILE_REVALIDATE
} from '@salesforce/retail-react-app/app/constants'
import {useHistory, useLocation, useParams} from 'react-router-dom'
import {useCustomToast} from '../../contexts/custom-toast-context'
import {useWishList} from '@salesforce/retail-react-app/app/hooks/use-wish-list'
import {getWishlistId, isWishlistMaxItemsReachedError} from '../../utils/wishlist-utils'
import {useProductLists} from '../../hooks/use-product-lists'
import {useScrollTo} from '../../hooks/use-scroll-to'
import {useProductAvailability} from '../../hooks/use-product-availability'

import {
    INTERACTION_LOCATION,
    resolveViewItemInteractionLocation,
    sendViewItemEvent,
    sendWishlistEvent,
    sendItemCartEvent
} from '../../utils/gtm-utils'
import {sendBrazeViewItemEvent, sendBrazeCartEvent, sendBrazeWishlistEvent} from '../../utils/braze-utils'
import {trackAddToBagEvent} from '../../utils/optimizely-web-exp-utils'
import {isHydrated, generateAlternateUrls} from '../../utils/utils'
import {getPriceData, findLowestPrice} from '../../utils/product-utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {getQVSelections, clearQVSelections} from '../../utils/quick-view-selection-storage'
import {useScript, setInitialScriptState} from '../../contexts/script-context'

// Empty product placeholder to prevent null reference errors in hooks during navigation
const EMPTY_PRODUCT = {variants: [], variationAttributes: [], variationGroups: []}

const ProductDetail = () => {
    const {enableScript, disableScript} = useScript()
    const {app: appConfig} = getConfig()
    const {formatMessage} = useIntl()
    const history = useHistory()
    const location = useLocation()
    const {sendViewProduct: sendCustomViewProduct} = useEinsteinViewProduct()
    const activeData = useActiveData()
    const {showCustomToast} = useCustomToast()
    const navigate = useNavigation()
    const customerId = useCustomerId()
    const {setProduct, setPageType} = useHeaderContext()
    const {brandId} = useBrand()
    const appOrigin = useAppOrigin()
    const {site, locale} = useMultiSite()
    const siteId = site?.id
    if (getSiteEnvVarValue(appConfig, 'IS_NOIBU_DISABLED_PDP', siteId) === 'true') {
        setInitialScriptState('noibu', false)
    }

    const {
        isOpen: isFindInStoreOpen,
        onOpen: onFindInStoreOpen,
        onClose: onFindInStoreClose
    } = useDisclosure()
    const {
        isOpen: isLongDescriptionOpen,
        onOpen: onLongDescriptionOpen,
        onClose: onLongDescriptionClose
    } = useDisclosure()
    const {ref: reviewsRef, scrollToElement: scrollToReviews} = useScrollTo(80)

    const {data: optimizelyData, isLoading: isOptimizelyLoading} = useOptimizelyPage('pdp')

    /* GDIPDPPage `BelowProductInfoArea` — renders between the product view and the
     * MoreToDiscover section. The same `pdp` CMS page is loaded as `optimizelyData`
     * and threaded into the product view, so the InformationAccordion can pull its
     * own BuyBox areas off the same object.
     * Targeting context (primaryCategoryId + parentCategoryTree) is built below
     * once `primaryCategory` is available; the actual extraction uses that. */

    useEffect(() => {
        const IS_NOIBU_DISABLED_PDP = getSiteEnvVarValue(appConfig, 'IS_NOIBU_DISABLED_PDP', siteId)
        if (IS_NOIBU_DISABLED_PDP === 'true') {
            disableScript('noibu')
        } else {
            enableScript('noibu')
        }

        return () => {
            if (IS_NOIBU_DISABLED_PDP) {
                enableScript('noibu')
            }
        }
    }, [])

    useEffect(() => {
        setPageType('product-detail')
        return () => setPageType(null)
    }, [setPageType])

    /****************************** Basket *********************************/
    const {data: basket, isLoading: isBasketLoading} = useCurrentBasket()
    const {data: customer} = useCurrentCustomer()
    const {addItemToNewOrExistingBasket} = useShopperBasketsMutationHelper()
    const updateItemsInBasketMutation = useShopperBasketsMutation('updateItemsInBasket')
    const {res} = useServerContext()
    setCacheControl(res, {
        maxAge: MAX_CACHE_AGE,
        staleWhileRevalidate: STALE_WHILE_REVALIDATE
    })
    /*************************** Product Detail and Category ********************/
    const {productId} = useParams()

    // Extract product name from URL path (e.g., /p/product-name/ID.html -> "product-name")
    // This is used to determine if we're navigating to a different product vs switching variations
    const getProductNameFromPath = (pathname) => {
        const match = pathname.match(/\/p\/([^/]+)\//)
        return match ? match[1] : null
    }

    // Track product transitions for skeleton loading on PDP-to-PDP navigation
    // Only show skeleton when navigating to a DIFFERENT product (product name changes)
    // Don't show skeleton when switching variations within the same product
    const currentProductName = getProductNameFromPath(location.pathname)
    const previousProductNameRef = useRef(currentProductName)
    const [isTransitioning, setIsTransitioning] = useState(false)
    const [gtmIds, setGTMIds] = useState([])

    // Always use productId from path - pid is not used in URL anymore
    const effectiveProductId = productId

    // Server-injected SEO meta (from req.seoData / window.__SEO_DATA__), used as the SSR
    // source for the Seo tags so title/description/canonical/og/hreflang appear in
    // view-source. Live product query data still takes precedence once available.
    const seoServerContext = useServerContext()
    const [seoMeta] = useState(() => {
        if (typeof window === 'undefined') return seoServerContext?.req?.seoData?.meta || null
        const injected = window.__SEO_DATA__
        if (!injected || injected.productId !== productId) return null
        return injected.meta || null
    })

    // Restore Quick View selections: if the user selected size/length in QV before navigating
    // here, pre-populate those URL params so the PDP opens with the same selections.
    // Only runs when effectiveProductId changes (i.e. on mount or product navigation).
    //
    // This is a ONE-SHOT handoff: the stored selection is consumed (cleared) immediately, so it
    // only applies to the single navigation that follows the Quick View selection. Without this,
    // a stored size would "stick" in localStorage (30-min TTL) and get re-applied on every later
    // visit to the same product — including fresh page loads, shared URLs and direct deep-links —
    // forcing a previously-selected (and possibly now out-of-stock) size onto a clean link.
    useEffect(() => {
        if (!effectiveProductId) return
        const stored = getQVSelections(effectiveProductId)
        if (!stored || Object.keys(stored).length === 0) return
        // Consume the handoff up front so it is never re-applied on subsequent visits.
        clearQVSelections(effectiveProductId)
        const searchParams = new URLSearchParams(location.search)
        let changed = false
        Object.entries(stored).forEach(([key, value]) => {
            if (value && !searchParams.has(key)) {
                searchParams.set(key, value)
                changed = true
            }
        })
        if (changed) {
            history.replace({...location, search: searchParams.toString()})
        }
    }, [effectiveProductId]) // eslint-disable-line react-hooks/exhaustive-deps

    // Detect PDP-to-PDP navigation synchronously during render to prevent keepPreviousData from
    // painting the previous product's layout before the new product resolves (e.g. set → standard).
    if (previousProductNameRef.current !== currentProductName && currentProductName) {
        previousProductNameRef.current = currentProductName
        if (!isTransitioning) {
            setIsTransitioning(true)
        }
    }

    // Per Salesforce best practices: availability should be fetched client-side only
    // "Frequently changing content, such as remaining inventory, is unsuitable for caching"
    // See: https://developer.salesforce.com/docs/commerce/pwa-kit-managed-runtime/guide/maximizing-your-cache-hit-ratio.html
    const isClient = typeof window !== 'undefined'
    const isSfccShopTheLook = usesSfccShopTheLook(appConfig, site?.id)

    const {
        data: product,
        isLoading: isProductLoading,
        isError: isProductError,
        error: productError,
        isPreviousData: isPreviousProductData
    } = useProduct(
        {
            parameters: {
                id: effectiveProductId,
                c_brand: brandId,
                perPricebook: true,
                expand: [
                    // 'availability' removed - fetched client-side below for fresh inventory
                    // 'options' removed - never read by the storefront
                    // 'recommendations' only when it feeds the Shop The Look carousel
                    ...(isSfccShopTheLook ? ['recommendations'] : []),
                    'promotions',
                    'prices',
                    'images',
                    'variations',
                    'set_products',
                    'bundled_products',
                    'page_meta_tags'
                ],
                allImages: true
            }
        },
        {
            // When shoppers select a different variant (and the app fetches the new data),
            // the old data is still rendered (and not the skeletons).
            keepPreviousData: true
        }
    )

    // Derived (not effect-set state) so gift card PDPs never mount the reviews and fire BV calls
    const isGiftCard = Boolean(product?.c_isGiftCard || product?.c_isEGiftCard)

    // PWA Kit swallows SSR fetch errors, so a render that lost the product still returns 200 with no
    // hero image in the markup - and the CDN then serves that to everyone for the full s-maxage
    // window. Mark it uncacheable so the cost is one slow request instead of ~30 minutes of them.
    // Overwrites the header set above; the good value is re-applied on every render pass, so this
    // only survives when the product genuinely failed to load.
    if (isProductError || !product) {
        setCacheControl(res, {uncacheable: true})
    }

    // Client-side only: Fetch fresh availability data after hydration via the dedicated
    // Shopper Availability API (leaner/faster than re-fetching the whole product document).
    // Returns top-level inventory (stockLevel, ats, orderable) plus per-variant orderable.
    // SFCC caches availability for 60 seconds by default.
    //
    // Request by MASTER id (when present) with expand=variations so a SINGLE call returns fresh
    // orderable for the URL product AND every sibling size in one round-trip. A variant /
    // variation-group requested by its own id does NOT expand (the response is just that entry),
    // which would otherwise leave the unselected size swatches on the stale product-document
    // `orderable` flag. Falls back to the URL product id for master/standalone products (which
    // already expand correctly).
    //
    // Gate on `product` being loaded (not just `effectiveProductId`): the master id lives on the
    // product document, so firing before it loads would request the URL/variation-group id first
    // and then re-fire with the master id once it resolves — two round-trips for the same data.
    // Waiting for `product` makes the request id stable, so this runs exactly once.
    // Real-time availability for the OPEN variation group, expanded to its own SKUs. Requesting the
    // group id (the URL product) with expand=variations returns the color's aggregate + one entry
    // per size in a SINGLE call — far leaner than the master (which expands to every color's
    // variants) and it fires straight from the URL id without waiting on `product.variants`.
    // Switching colors re-fires it invisibly; nothing gates the UI on it (sizes are seeded from the
    // product document below and only patched when this resolves).
    const {data: skuAvailabilityMap} = useProductAvailability(effectiveProductId, {
        expandVariations: true,
        enabled: isClient && Boolean(effectiveProductId)
    })
    const availabilityData = useMemo(() => {
        const result = skuAvailabilityMap?.[effectiveProductId]
        if (!result) return undefined
        return {
            inventory: result.inventory,
            inventoryId: result.inventoryId,
            variants: result.variants
        }
    }, [skuAvailabilityMap, effectiveProductId])

    // Per-color orderable from the product document (variationAttributes → color values). It's
    // accurate enough to hide sold-out colors on mount, so we seed the swatch filter from it (below)
    // — no flicker, no render block.
    const docColorOrderable = useMemo(() => {
        const colorAttr = (product?.variationAttributes || []).find((a) => a.id === 'color')
        const map = {}
        ;(colorAttr?.values || []).forEach((v) => {
            map[v.value] = v.orderable
        })
        return map
    }, [product?.variationAttributes])

    // Defer the all-colors stock check until AFTER the main content has painted (post-LCP). It's a
    // non-critical, potentially multi-batch call (a 58-color master needs 3 × 24-id requests), so
    // running it during the critical render would contend with LCP. `requestIdleCallback` (with a
    // timeout fallback) is a robust "page has settled" signal. The swatch row is already correct from
    // the document seed; when this lands it reconciles both directions — hides colors sold out since
    // the 24h-cached doc, reveals restocked ones — which may cause a very small swatch-row CLS. That
    // trade is accepted for real-time accuracy (recovers the sales lost to stale cached stock).
    const [isPostLcp, setIsPostLcp] = useState(false)
    useEffect(() => {
        if (typeof window === 'undefined') return undefined
        const ric = window.requestIdleCallback
        if (ric) {
            const id = ric(() => setIsPostLcp(true), {timeout: 2000})
            return () => window.cancelIdleCallback?.(id)
        }
        const t = setTimeout(() => setIsPostLcp(true), 1500)
        return () => clearTimeout(t)
    }, [])

    // Confirm real-time stock for EVERY color (variation group) — including colors the 24h-cached
    // document still shows out of stock but that have since restocked. `product.variants` only holds
    // the open color's variants, so a color's aggregate stock can't be derived client-side — hence
    // one availability entry per color group. Notify-me colors stay visible via c_notifyMeColors
    // regardless of the result.
    const variationGroupIds = useMemo(
        () => (product?.variationGroups || []).map((vg) => vg.productId).filter(Boolean),
        [product?.variationGroups]
    )
    const {data: colorAvailabilityMap} = useProductAvailability(variationGroupIds, {
        enabled: isClient && isPostLcp && variationGroupIds.length > 0
    })

    // The whole selector structure mounts from the product document (orderability seeded in
    // productWithAvailability below), so the availability-dependent UI never needs a skeleton — the
    // live SKU / color calls only patch real-time orderability in as they resolve.
    const isAvailabilityPending = false

    // Build the fully-structured product the selectors render from. Orderability is SEEDED from the
    // product document (`variationAttributes[].values[].orderable`) so the whole variant/color/size
    // structure mounts correct from SSR — no skeleton — then patched with real-time truth:
    //   - per-SKU orderable from the open color's availability call (availabilityData.variants)
    //   - per-color aggregate orderable from the all-colors call (colorAvailabilityMap)
    // Generic across attributes: a variant is seeded orderable only when every one of its variation
    // values is orderable per the document.
    const productWithAvailability = useMemo(() => {
        if (!product) return null

        // Seed each color's orderable from the document, then override with the live confirmation
        // when it lands. This hides sold-out colors on mount (no flicker) while still correcting a
        // color that has since sold out.
        const mergedVariationGroups = product.variationGroups?.map((vg) => {
            const seeded = docColorOrderable[vg.variationValues?.color]
            const live = colorAvailabilityMap?.[vg.productId]?.inventory?.orderable
            const orderable = live === undefined ? seeded : live
            return orderable === undefined ? vg : {...vg, orderable}
        })

        const liveOrderable = new Map(
            (availabilityData?.variants || []).map((v) => [v.productId, v.orderable])
        )
        const mergedVariants = product.variants?.map((variant) => {
            const seeded = (product.variationAttributes || []).every((attr) => {
                const match = attr.values?.find(
                    (val) => val.value === variant.variationValues?.[attr.id]
                )
                return match ? match.orderable !== false : true
            })
            const live = liveOrderable.get(variant.productId)
            return {...variant, orderable: live === undefined ? seeded : live}
        })

        return {
            ...product,
            ...(availabilityData?.inventory
                ? {inventory: availabilityData.inventory, inventoryId: availabilityData.inventoryId}
                : {}),
            variants: mergedVariants || product.variants,
            variationGroups: mergedVariationGroups || product.variationGroups
            // Notify-me / few-items-left custom attributes are carried by `product`
            // (the Shopper Products GET adds them on every response).
        }
    }, [product, availabilityData, colorAvailabilityMap, docColorOrderable])

    // Get variant from the merged product (with fresh orderable status when available)
    // Use EMPTY_PRODUCT placeholder to prevent null reference errors during navigation
    const variant = useVariant(productWithAvailability || EMPTY_PRODUCT)

    // NOTE: The PDP intentionally does NOT persist its selections back to localStorage. The
    // Quick View is the sole writer (it stores the shopper's explicit selection at the moment
    // it is made, right before navigating here). Persisting on every PDP variant resolution
    // caused a stale size to "stick" and be re-applied to fresh/direct/shared links via the
    // one-shot restore above. Keeping the PDP read-only makes restoration a genuine Quick
    // View → PDP handoff rather than a lingering, self-reinforcing preference.

    // This ensures promotional prices are loaded for the specific variant (size selection)
    // BD team requirement: when switching variants, call the product API with promotions expand
    const {data: variantProductData} = useProduct(
        {
            parameters: {
                id: variant?.productId,
                perPricebook: true,
                expand: ['prices', 'promotions']
            }
        },
        {
            enabled: isClient && Boolean(variant?.productId),
            keepPreviousData: false
        }
    )

    const isVariantProductDataFresh = useMemo(() => {
        if (!variant?.productId || !variantProductData) return false

        const variantDataId = variantProductData.productId || variantProductData.id
        return variantDataId === variant.productId
    }, [variant?.productId, variantProductData])

    // When a PDP is opened via a variant SKU URL (e.g. clicking a product name in the cart), the
    // loaded product is a specific variant. In this catalog both `primaryCategoryId` and the
    // category-scoped promotions live on the variation GROUP — the variant document carries
    // neither (the master's primary category is the site root, which yields no breadcrumb).
    // Product listing tiles link to the variation group id, but the cart links to the variant id,
    // so resolve the variant's own variation group — matching on colour, since `variationGroups`
    // lists every colour of the master — and reuse it for the breadcrumb category and the
    // promotion callout alike.
    const variationGroupId = useMemo(() => {
        const variationGroups = product?.variationGroups
        if (!variationGroups?.length) return undefined
        if (!product?.type?.variant) return variationGroups[0]?.productId

        const matchingGroup = variationGroups.find(
            (group) => group?.variationValues?.color === product?.variationValues?.color
        )
        return (matchingGroup || variationGroups[0])?.productId
    }, [product?.variationGroups, product?.variationValues?.color, product?.type?.variant])

    const needsGroupCategory = Boolean(product && !product.primaryCategoryId && variationGroupId)
    const needsGroupPromotions = Boolean(product?.type?.variant && variationGroupId)
    const {data: variationGroupProduct} = useProduct(
        {
            parameters: {
                id: variationGroupId,
                expand: ['promotions'],
                select: '(id,primaryCategoryId,productPromotions)'
            }
        },
        {
            enabled: isClient && (needsGroupCategory || needsGroupPromotions),
            keepPreviousData: true
        }
    )

    // SFCC only returns the promotions that resolve against the requested document, so a variant
    // is missing the group's category-scoped promotions (the ones carrying the PDP callout).
    // Merge those in for display only: `promotionalPrice` is deliberately dropped so pricing
    // keeps coming from the variant itself.
    const productWithGroupPromotions = useMemo(() => {
        if (!productWithAvailability) return null

        const groupPromotions = variationGroupProduct?.productPromotions
        if (!product?.type?.variant || !groupPromotions?.length) return productWithAvailability

        const ownPromotionIds = new Set(
            (productWithAvailability.productPromotions || []).map(
                (promotion) => promotion?.promotionId
            )
        )
        const missingPromotions = groupPromotions
            .filter(
                (promotion) => promotion?.promotionId && !ownPromotionIds.has(promotion.promotionId)
            )
            .map((promotion) => ({
                promotionId: promotion.promotionId,
                calloutMsg: promotion.calloutMsg
            }))

        if (!missingPromotions.length) return productWithAvailability

        return {
            ...productWithAvailability,
            productPromotions: [
                ...(productWithAvailability.productPromotions || []),
                ...missingPromotions
            ]
        }
    }, [productWithAvailability, variationGroupProduct?.productPromotions, product?.type?.variant])

    // Until a size is picked, the price on screen belongs to the cheapest variant, and the variant
    // documents are the only place SCAPI exposes `c_newArrivalDate` - so resolve that one variant
    // to keep the "price as of" disclosure alongside the struck-through price.
    const priceVariantId = useMemo(() => {
        if (variant?.productId || !/[-_]US$/i.test(locale?.id || '')) return undefined

        const {currentPrice, listPrice} = getPriceData(productWithGroupPromotions || {})
        if (!listPrice || listPrice === currentPrice) return undefined

        return findLowestPrice(productWithGroupPromotions)?.data?.productId
    }, [variant?.productId, productWithGroupPromotions, locale?.id])

    const {data: priceVariantProduct} = useProduct(
        {
            parameters: {
                id: priceVariantId,
                select: '(id,c_newArrivalDate)'
            }
        },
        {
            enabled: isClient && Boolean(priceVariantId),
            keepPreviousData: false
        }
    )

    const priceVariantArrivalDate =
        priceVariantId && priceVariantProduct?.id === priceVariantId
            ? priceVariantProduct.c_newArrivalDate
            : undefined

    // Merge variant promotional data into the product
    const productWithPromotions = useMemo(() => {
        if (!productWithGroupPromotions) return null

        // If we have variant-level promotions, merge them into that specific variant only
        // DO NOT overwrite product-level productPromotions - that would apply the wrong
        // promotional price to other variants in findLowestPrice
        if (
            isVariantProductDataFresh &&
            variantProductData?.productPromotions &&
            variant?.productId
        ) {
            // Update only the selected variant with its promotional data
            const updatedVariants = productWithGroupPromotions.variants?.map((v) => {
                if (v.productId === variant.productId) {
                    return {
                        ...v,
                        productPromotions: variantProductData.productPromotions,
                        tieredPrices: variantProductData.tieredPrices || v.tieredPrices,
                        c_newArrivalDate: variantProductData.c_newArrivalDate
                    }
                }
                return v
            })

            return {
                ...productWithGroupPromotions,
                id:
                    variantProductData.id ||
                    variantProductData.productId ||
                    productWithGroupPromotions.id,
                productId: variantProductData.productId || variant.productId,
                variants: updatedVariants || productWithGroupPromotions.variants
                // Keep original product-level productPromotions unchanged
            }
        }

        // SCAPI omits custom attributes from `variants[]`, so the variant's own document is the
        // only source of `c_newArrivalDate` - which drives the US "price as of" disclosure.
        if (
            isVariantProductDataFresh &&
            variantProductData?.c_newArrivalDate &&
            variant?.productId
        ) {
            return {
                ...productWithGroupPromotions,
                variants: productWithGroupPromotions.variants?.map((v) =>
                    v.productId === variant.productId
                        ? {...v, c_newArrivalDate: variantProductData.c_newArrivalDate}
                        : v
                )
            }
        }

        if (priceVariantArrivalDate) {
            return {...productWithGroupPromotions, c_newArrivalDate: priceVariantArrivalDate}
        }

        return productWithGroupPromotions
    }, [
        productWithGroupPromotions,
        variantProductData,
        variant?.productId,
        isVariantProductDataFresh,
        priceVariantArrivalDate
    ])

    const isProductASet = product?.type.set
    const isProductABundle = product?.type.bundle
    const {variationAttributes, isOutOfStock: storefrontIsOutOfStock} = useDerivedProduct(
        productWithPromotions || product,
        isProductASet,
        isProductABundle
    )

    // Build a schema that reflects what the storefront is currently showing.
    // This avoids trusting stale/incoherent c_schemaMarkup payloads from variantProductData.
    const activeSchemaMarkup = useMemo(() => {
        const normalizeLocaleTag = (value) =>
            String(value || '')
                .trim()
                .toLowerCase()
                .replace('_', '-')

        const resolveLocaleProductUrl = (canonicalUrl, alternateUrls, activeLocale) => {
            const localeCandidates = [activeLocale?.alias, activeLocale?.id]
                .map(normalizeLocaleTag)
                .filter(Boolean)

            if (!localeCandidates.length || !Array.isArray(alternateUrls)) {
                return canonicalUrl
            }

            const localizedMatch = alternateUrls.find((entry) => {
                const hreflang = normalizeLocaleTag(entry?.hreflang)
                return hreflang && localeCandidates.includes(hreflang)
            })

            return localizedMatch?.url || canonicalUrl
        }

        const safeParse = (value) => {
            if (!value) return null
            if (typeof value === 'string') {
                try {
                    return JSON.parse(value)
                } catch (e) {
                    return null
                }
            }
            return typeof value === 'object' ? value : null
        }

        const productIdInContext = product?.id || ''
        // Always attempt to extract variant schema from available data, not just when fresh
        // This ensures schema is available during SSR even if variant data is still loading
        const variantSchema = safeParse(variantProductData?.c_schemaMarkup) ||
            safeParse(variant?.c_schemaMarkup)

        const variantSchemaLooksCoherent = (() => {
            if (!variantSchema || !productIdInContext) return false
            const marker = [
                variantSchema.sku,
                variantSchema.mpn,
                variantSchema['@id'],
                variantSchema.url
            ]
                .filter(Boolean)
                .join(' ')
            return marker.includes(productIdInContext)
        })()

        const baseSchema =
            (variantSchemaLooksCoherent && variantSchema) ||
            safeParse(variant?.c_schemaMarkup) ||
            safeParse(product?.c_schemaMarkup)

        // Compute availability early so it can be used in the fallback schema
        // This logic mirrors the availability computation done later for offers
        const selectedValuesFromAttributes = (variationAttributes || []).reduce((acc, attr) => {
            const selectedValue = attr?.selectedValue?.value
            if (attr?.id && selectedValue) {
                acc[attr.id] = selectedValue
            }
            return acc
        }, {})

        const selectedVariationValues = {
            ...(product?.variationValues || {}),
            ...selectedValuesFromAttributes,
            ...(variant?.variationValues || {})
        }

        const selectedScopedVariants = (productWithAvailability?.variants || []).filter((v) =>
            Object.entries(selectedVariationValues).every(([id, selectedValue]) => {
                if (!selectedValue) return true
                return v?.variationValues?.[id] === selectedValue
            })
        )

        // When color is selected, check if there are available sizes for that color
        // If any variant for the selected color is not explicitly marked as out-of-stock, show as InStock
        const inferredScopedOrderable = selectedScopedVariants.length > 0
            ? selectedScopedVariants.some((v) => v?.orderable !== false)
            : undefined

        const variationDimensionIds = (product?.variationAttributes || [])
            .map((attr) => attr?.id)
            .filter((id) => id && id !== 'color')

        const buildVariantKey = (variationValues = {}) =>
            variationDimensionIds.map((id) => `${id}:${variationValues?.[id] || ''}`).join('|')

        const scopedVariantKeys = new Set(
            selectedScopedVariants.map((v) => buildVariantKey(v?.variationValues)).filter(Boolean)
        )

        const notifyMeArray =
            productWithAvailability?.c_notifyMeArray || product?.c_notifyMeArray || []
        const notifyMeKeys = new Set(
            notifyMeArray
                .map((entry) =>
                    variationDimensionIds.map((id) => `${id}:${entry?.[id] || ''}`).join('|')
                )
                .filter(Boolean)
        )

        const isNotifyMeCoveringAllScopedVariants =
            scopedVariantKeys.size > 0 &&
            [...scopedVariantKeys].every((key) => notifyMeKeys.has(key))

        const inferredScopedOrderableByNotifyMe =
            scopedVariantKeys.size > 0 ? !isNotifyMeCoveringAllScopedVariants : undefined

        const inferredFromUnselectedAttributes = (() => {
            const unselectedAttributes = (variationAttributes || []).filter((attr) => {
                if (!attr?.id || attr.id === 'color') return false
                if (attr?.selectedValue?.value) return false
                return Array.isArray(attr?.values) && attr.values.length > 0
            })

            if (!unselectedAttributes.length) return undefined

            const everyUnselectedAttrHasOrderableOption = unselectedAttributes.every((attr) =>
                attr.values.some((value) => value?.orderable)
            )

            return everyUnselectedAttrHasOrderableOption
        })()

        // Compute orderable status early
        const orderable =
            (variant?.productId ? variant?.orderable : undefined) ??
            inferredScopedOrderable ??
            inferredScopedOrderableByNotifyMe ??
            inferredFromUnselectedAttributes ??
            (typeof storefrontIsOutOfStock === 'boolean' ? !storefrontIsOutOfStock : undefined) ??
            product?.inventory?.orderable

        // Compute availability URL early for use in fallback schema
        // If we have explicit orderable data from variant or product, or inferred from scoped variants, use it
        // Otherwise fall back to storefrontIsOutOfStock for product-level stock check
        const hasExplicitOrderableData =
            (variant?.productId && typeof variant?.orderable === 'boolean') ||
            typeof inferredScopedOrderable === 'boolean' ||
            typeof product?.inventory?.orderable === 'boolean'

        const earlyAvailabilityUrl = hasExplicitOrderableData
            ? (orderable === false
                ? 'https://schema.org/OutOfStock'
                : 'https://schema.org/InStock')
            : (storefrontIsOutOfStock
                ? 'https://schema.org/OutOfStock'
                : 'https://schema.org/InStock')

        // Create fallback schema with dynamic availability
        const fallbackSchema = product?.id && {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: product?.name,
            sku: product?.id,
            url: product?.c_canonicalURL || '',
            offers: {
                '@type': 'Offer',
                availability: earlyAvailabilityUrl,
                priceCurrency: 'USD',
                price: String(variant?.price ?? product?.price ?? '0')
            }
        }

        if (!baseSchema && !fallbackSchema) {
            // Final fallback: return raw c_schemaMarkup if available
            return safeParse(product?.c_schemaMarkup) || null
        }

        const nextSchema = {...(baseSchema || fallbackSchema)}

        const existingOfferAvailability = (() => {
            if (Array.isArray(nextSchema?.offers)) {
                return nextSchema.offers[0]?.availability
            }
            return nextSchema?.offers?.availability
        })()

        // Apply computed availability to offers - use earlyAvailabilityUrl which
        // reflects the actual orderable status from server data
        const availabilityUrl = earlyAvailabilityUrl

        const existingOfferPrice = (() => {
            const price = Array.isArray(nextSchema?.offers)
                ? nextSchema.offers[0]?.price
                : nextSchema?.offers?.price

            return price === '' ? null : price
        })()

        const displayPrice = existingOfferPrice ?? variant?.price ?? product?.price

        const offersPatch = {availability: availabilityUrl}
        if (displayPrice != null) {
            offersPatch.price = String(displayPrice)
        }

        nextSchema.offers = Array.isArray(nextSchema.offers)
            ? nextSchema.offers.map((offer) => ({...offer, ...offersPatch}))
            : nextSchema.offers
            ? {...nextSchema.offers, ...offersPatch}
            : nextSchema.offers

        const selectedColor = selectedVariationValues?.color
        const scopedGroups = (product?.imageGroups || []).filter((group) => {
            const colorAttr = group?.variationAttributes?.find((attr) => attr.id === 'color')
            if (!selectedColor) return true
            return colorAttr?.values?.some((v) => v.value === selectedColor)
        })
        const largeGroup = scopedGroups.find((group) => group.viewType === 'large')
        const fallbackGroup = scopedGroups[0]
        const images = (largeGroup || fallbackGroup)?.images?.map((img) => img.link).filter(Boolean)
        if (images?.length) {
            nextSchema.image = images
        }

        const localizedSchemaUrl = resolveLocaleProductUrl(
            product?.c_canonicalURL,
            product?.c_alternateURLs,
            locale
        )

        if (localizedSchemaUrl) {
            nextSchema.url = localizedSchemaUrl
            nextSchema['@id'] = localizedSchemaUrl
        }

        return nextSchema
    }, [
        variantProductData?.c_schemaMarkup,
        variant?.c_schemaMarkup,
        variant?.orderable,
        variant?.price,
        variant?.productId,
        variant?.variationValues?.color,
        product?.id,
        product?.c_schemaMarkup,
        product?.inventory?.orderable,
        product?.price,
        product?.variationAttributes,
        product?.name,
        variationAttributes,
        storefrontIsOutOfStock,
        productWithAvailability?.variants,
        productWithAvailability?.c_notifyMeArray,
        product?.c_notifyMeArray,
        product?.imageGroups,
        product?.variationValues?.color,
        product?.c_canonicalURL,
        product?.c_alternateURLs,
        locale?.alias,
        locale?.id,
        isAvailabilityPending
    ])

    const pCatId = product?.primaryCategoryId || variationGroupProduct?.primaryCategoryId

    // Note: Since category needs id from product detail, it can't be server side rendered atm
    // until we can do dependent query on server
    const {
        data: category,
        isError: isCategoryError,
        error: categoryError
    } = useCategory({
        parameters: {
            id: pCatId,
            levels: 1
        }
    })

    // Category is breadcrumbs and metadata. Log the failure and keep the product,
    // including when the category itself is missing.
    useEffect(() => {
        if (!isCategoryError) return
        const status = categoryError?.response?.status ?? 'n/a'
        console.error(
            `[PDP] category lookup failed status=${status} message=${categoryError?.message ?? ''}`
        )
    }, [isCategoryError, categoryError])

    /****************************** Sets and Bundles *********************************/
    const [childProductSelection, setChildProductSelection] = useState({})
    const [childProductOrderability, setChildProductOrderability] = useState({})
    const [selectedBundleQuantity, setSelectedBundleQuantity] = useState(1)
    const childProductRefs = React.useRef({})
    let bundleChildVariantIds = ''
    if (isProductABundle)
        bundleChildVariantIds = Object.keys(childProductSelection)
            ?.map((key) => childProductSelection[key].variant.productId)
            .join(',')

    const {data: bundleChildrenData} = useProducts(
        {
            parameters: {
                ids: bundleChildVariantIds,
                allImages: false,
                expand: ['variations'],
                select: '(data.(id,master))'
            }
        },
        {
            enabled: bundleChildVariantIds?.length > 0,
            keepPreviousData: true
        }
    )

    // Fresh inventory for the selected bundle-child variants via the Shopper Availability API.
    const {data: bundleChildAvailability} = useProductAvailability(
        bundleChildVariantIds ? bundleChildVariantIds.split(',') : [],
        {enabled: Boolean(bundleChildVariantIds?.length)}
    )

    if (isProductABundle && bundleChildrenData) {
        // Loop through the bundle children and update the inventory for variant selection
        product.bundledProducts.forEach(({product: childProduct}, index) => {
            const matchingChildProduct = bundleChildrenData.data.find(
                (bundleChild) => bundleChild.master.masterId === childProduct.id
            )
            if (matchingChildProduct) {
                product.bundledProducts[index].product = {
                    ...childProduct,
                    inventory: bundleChildAvailability?.[matchingChildProduct.id]?.inventory
                }
            }
        })
    }

    const comboProduct = isProductASet || isProductABundle ? normalizeSetBundleProduct(product) : {}

    const pageTitle = product?.pageMetaTags?.find((option) => option.id === 'title')?.value || ''

    // SSR fallback for SEO tags: prefer live product query data, fall back to the
    // server-injected seoMeta so the tags render in view-source before hydration.
    const seoMetaTags = product?.pageMetaTags ?? seoMeta?.pageMetaTags ?? []
    const seoTitle =
        pageTitle ||
        seoMetaTags.find((tag) => tag.id === 'title')?.value ||
        product?.pageTitle ||
        seoMeta?.pageTitle
    const seoCanonicalUrl = product?.c_canonicalURL ?? seoMeta?.canonicalUrl
    const seoAlternateURLs = product?.c_alternateURLs ?? seoMeta?.alternateURLs
    // Derive the SEO brand from the product data (identical on SSR and client). Hostname-based
    // useBrand defaults to garage on SSR when the host has no brand slug (e.g. plain localhost).
    const seoBrandId = product?.brand?.toLowerCase() || brandId

    // Preload the hero image (the LCP element) from og:image, which comes from the same
    // SEO fallback chain above and is therefore present even when the product query does
    // not resolve during SSR. Without this, that render ships no <img> and the browser
    // only discovers the hero after React injects it — at Low priority, seconds late.
    const lcpImagePreloadAttrs = useMemo(() => {
        const ogImage = seoMetaTags.find((tag) => tag.id === 'og:image')?.value
        if (!ogImage) return null
        const baseUrl = ogImage.split('?')[0]
        const enableResizeParams =
            getSiteEnvVarValue(appConfig, 'PRODUCT_IMGS_RESIZE_PARAMS_ENABLED', siteId) == 'true'
        if (!enableResizeParams) return {href: baseUrl}
        // Match the gallery's srcset/sizes exactly so the preload is reused, not duplicated.
        const {src, srcSet, sizes} = getResponsiveImageAttributes({
            src: `${baseUrl}[?sw={width}&q=60]`,
            widths: IMAGE_WIDTHS.large
        })
        return {href: src, imagesrcset: srcSet, imagesizes: sizes}
    }, [seoMetaTags, appConfig, siteId])

    const [primaryCategory, setPrimaryCategory] = useState(category)
    const pdpLoyaltyPoints = useMemo(() => {
        const currentPrice = getPriceData(productWithPromotions).currentPrice
        const pointsBase = currentPrice > 0 ? currentPrice : productWithPromotions?.c_loyaltyPointsToEarn

        return pointsBase != null
            ? Math.round(Number(pointsBase)) * (customer?.c_doublePointsEnabled ? 2 : 1)
            : undefined
    }, [productWithPromotions, customer?.c_doublePointsEnabled])

    /**************** Product Variant ****************/
    const [productUrl, setProductUrl] = useState(null)

    useEffect(() => {
        // Just track the product URL for sharing/analytics - no pid needed in URL
        // pid is only needed at add-to-cart time, not for navigation
        setProductUrl(appOrigin + location.pathname)
    }, [location.pathname])

    // This page uses the `primaryCategoryId` to retrieve the category data. This attribute
    // is only available on `master` products. Since a variation will be loaded once all the
    // attributes are selected (to get the correct inventory values), the category information
    // is overridden. This will allow us to keep the initial category around until a different
    // master product is loaded.
    useEffect(() => {
        if (category) {
            setPrimaryCategory(category)
        }
    }, [category])

    // Targeting context for GDIPDPPage CMS wrappers (Category ID / Product ID).
    const pdpCmsContext = useMemo(
        () => ({
            primaryCategoryId: product?.primaryCategoryId,
            parentCategoryTree: primaryCategory?.parentCategoryTree || [],
            productId: product?.id,
            masterProductId: product?.master?.masterId,
            variantProductId: variant?.productId
        }),
        [
            product?.primaryCategoryId,
            primaryCategory?.parentCategoryTree,
            product?.id,
            product?.master?.masterId,
            variant?.productId
        ]
    )

    const belowProductInfoBlocks = useMemo(
        () => extractPDPAreaBlocks(optimizelyData?.item?.BelowProductInfoArea, pdpCmsContext),
        [optimizelyData, pdpCmsContext]
    )

    // Clear transitioning state when new product data is loaded
    useEffect(() => {
        if (product && isTransitioning) {
            // Check if product matches either the variant ID (pid) or the variation group ID (path)
            const matchesEffectiveId = product.id === effectiveProductId
            const matchesPathId = product.id?.includes(productId.split('.')[0])
            if (matchesEffectiveId || matchesPathId) {
                setIsTransitioning(false)
            }
        }
    }, [product, isTransitioning, productId, effectiveProductId])

    /**************** Wishlist ****************/
    const {data: wishlist, isLoading: isWishlistLoading, refetch: refetchWishlist} = useWishList()
    const {customFolders = []} = useProductLists()
    const createCustomerProductListItem = useShopperCustomersMutation(
        'createCustomerProductListItem'
    )
    const createCustomerProductList = useShopperCustomersMutation('createCustomerProductList')
    const {mutateAsync: deleteCustomerProductListItem} = useShopperCustomersMutation(
        'deleteCustomerProductListItem'
    )

    const isInWishlist = useMemo(() => {
        if (!wishlist?.customerProductListItems || !product) return false

        return !!wishlist.customerProductListItems.find(
            (i) => i.productId === variant?.productId || i.productId === product?.id
        )
    }, [wishlist?.customerProductListItems, variant?.productId, product?.id])

    const handleAddToWishlist = async (productArg, variantArg, quantity) => {
        const listId = await getWishlistId({
            wishlist,
            customerId,
            refetchWishlist,
            createCustomerProductList
        })
        if (!listId || !customerId) {
            showError()
            return
        }

        // Use the outer-scope variant/product for the wishlist lookup so the check is
        // always consistent with the isInWishlist memo (both derived from the same render).
        // The parameters may shadow those variables and diverge if ProductView is on a
        // slightly different render cycle when the user clicks.
        const itemInWishlist = wishlist?.customerProductListItems?.find(
            (i) => i.productId === variant?.productId || i.productId === product?.id
        )

        if (!itemInWishlist) {
            createCustomerProductListItem.mutate(
                {
                    parameters: {
                        listId,
                        customerId
                    },
                    body: {
                        // NOTE: API does not respect quantity, it always adds 1
                        quantity,
                        productId: variantArg?.productId || productArg?.id,
                        public: false,
                        priority: 1,
                        type: 'product'
                    }
                },
                {
                    // wishlist success
                    onSuccess: () => {
                        showCustomToast({
                            message: formatMessage({
                                id: 'cart.info.item_added',
                                defaultMessage: 'Added to Wishlist'
                            }),
                            actionLabel: formatMessage({
                                id: 'cart.info.view_list',
                                defaultMessage: 'View List'
                            }),
                            onActionClick: () => navigate('/account/wishlist')
                        })
                        // GTM Modification
                        sendWishlistEvent(productArg, false, null, INTERACTION_LOCATION.PDP)

                        // Braze Modification - Send Braze Wishlist Event
                        sendBrazeWishlistEvent(productArg, false, productArg?.primaryCategoryId)
                    },
                    onError: async (error) => {
                        if (await isWishlistMaxItemsReachedError(error)) {
                            showCustomToast({
                                message: formatMessage(
                                    {
                                        id: 'cart.info.full_message',
                                        defaultMessage:
                                            'You have reached the maximum number of items for your Wishlist. <a>See Wishlist</a>'
                                    },
                                    {
                                        a: (chunks) => (
                                            <Link
                                                href="/account/wishlist"
                                                display="block"
                                                mt={2}
                                                textDecoration="underline"
                                            >
                                                {chunks}
                                            </Link>
                                        )
                                    }
                                )
                            })
                        } else {
                            showError()
                        }
                    }
                }
            )
        } else {
            if (!wishlist?.id) {
                showError()
                return
            }

            try {
                await deleteCustomerProductListItem({
                    parameters: {
                        listId: wishlist.id,
                        customerId,
                        itemId: itemInWishlist.id
                    }
                })

                // Keep folder/collection state in sync when removing via PDP.
                const folderRemovals = customFolders.flatMap((folder) => {
                    const folderItemId = folder.customerProductListItems?.find(
                        (i) => i.productId === itemInWishlist.productId
                    )?.id
                    return folderItemId ? [{listId: folder.id, itemId: folderItemId}] : []
                })

                for (const {listId: folderListId, itemId: folderItemId} of folderRemovals) {
                    await deleteCustomerProductListItem({
                        parameters: {
                            listId: folderListId,
                            customerId,
                            itemId: folderItemId
                        }
                    })
                }

                showCustomToast({
                    message: formatMessage({
                        id: 'wishlist.info.item_removed',
                        defaultMessage: 'Item removed'
                    })
                })
                // GTM Modification
                sendWishlistEvent(productArg, true, null, INTERACTION_LOCATION.PDP)

                // Braze Modification - Send Braze Wishlist Event
                sendBrazeWishlistEvent(productArg, true, productArg?.primaryCategoryId)
            } catch {
                showError()
            }
        }
    }

    const [findInStoreModalTitle, setFindInStoreModalTitle] = useState('')
    const [isVariantSelected, setIsVariantSelected] = useState(variant?.productId ? true : false)

    // Auto-select single-option attributes (size or length)
    useEffect(() => {
        if (!variationAttributes || variationAttributes.length === 0) return

        variationAttributes.forEach((attr) => {
            // Only auto-select for size or length attributes
            if (attr.id !== 'size' && attr.id !== 'length') return

            // Skip if already selected
            if (attr.selectedValue?.value) return

            // Check if there's only one available option
            const availableValues = attr.values?.filter((v) => v.orderable) || []
            if (availableValues.length === 1) {
                const singleValue = availableValues[0]
                // Navigate to the URL with the auto-selected value
                if (singleValue.href) {
                    history.replace(singleValue.href)
                }
            }
        })
    }, [variationAttributes, history])

    /**************** Add To Cart ****************/
    const showError = () => {
        showCustomToast({
            message: formatMessage(API_ERROR_MESSAGE)
        })
    }

    const getMatchingGroup = (product) => {
        const matchingGroup = product?.variationGroups?.find(
            (group) => group?.variationValues?.color === product?.variationValues?.color
        )

        if (matchingGroup) {
            return matchingGroup.productId
        }

        return null
    }

    const handleAddToCart = async (productSelectionValues, giftCardData = null) => {
        try {
            const productItems = productSelectionValues.map(({variant, quantity}) => {
                const baseItem = {
                    productId: variant.productId,
                    productName: product?.name,
                    price: variant.price,
                    c_brand: product?.brand,
                    c_currency: product?.currency,
                    c_categories: product?.c_categories,
                    c_refinementColor: product?.c_refinementColor,
                    c_size: product?.c_size || variant.variationValues?.size,
                    c_variationGroup: getMatchingGroup(product),
                    quantity,
                    c_itemAddLocation: INTERACTION_LOCATION.PDP
                }

                if (product?.c_isGiftCard || product?.c_isEGiftCard) {
                    return {
                        ...baseItem,
                        c_isGiftCard: product.c_isGiftCard || false,
                        c_isEGiftCard: product.c_isEGiftCard || false,
                        ...(giftCardData || {})
                    }
                }

                return baseItem
            })

            const updatedBasket = await addItemToNewOrExistingBasket(productItems)

            // GTM Modification: Explicitly set c_gtm_index on product to ensure it takes
            // priority in sendItemCartEvent's resolution logic and isn't overridden by cached values
            if (initialGTMIndex >= 0) {
                productItems[0].c_gtm_index = initialGTMIndex
            }

            let productAdded = updatedBasket.productItems.find(item => item.productId === productItems[0].productId)
            sendItemCartEvent(
                {
                    ...productAdded,
                    c_categories: productAdded?.c_categories || productItems[0]?.c_categories
                },
                false,
                null,
                INTERACTION_LOCATION.PDP,
                updatedBasket.currency
            )

            // Braze Modification - Send Braze Cart Event
            const brazeProduct = {
                ...productItems[0],
                imageGroups: productSelectionValues[0]?.product?.imageGroups || [],
                c_color: productSelectionValues[0]?.product?.c_color || '',
                c_relativeUrl: productSelectionValues[0]?.product?.c_relativeUrl || ''
            }
            sendBrazeCartEvent(updatedBasket, brazeProduct, 'add')

            // ATB Optimizely Web Experimentation
            trackAddToBagEvent({
                sku: productItems[0].productId,
                value: productItems[0].price,
                category: productItems[0].c_categories,
                source: 'product-detail'
            })

            // If the items were successfully added, set the return value to be used
            // by the add to cart modal.
            return productSelectionValues
        } catch (error) {
            // Return null so ProductView can handle the error display (e.g. max qty inline error).
            // Showing a toast here would duplicate the feedback already shown by ProductView.
            return null
        }
    }

    /**************** Product Set/Bundles Handlers ****************/
    const handleChildProductValidation = useCallback(() => {
        // Run validation for all child products. This will ensure the error
        // messages are shown.
        Object.values(childProductRefs.current).forEach(({validateOrderability}) => {
            validateOrderability({scrollErrorIntoView: false})
        })

        // Using ot state for which child products are selected, scroll to the first
        // one that isn't selected.
        const selectedProductIds = Object.keys(childProductSelection)
        const firstUnselectedProduct = comboProduct.childProducts.find(
            ({product: childProduct}) => !selectedProductIds.includes(childProduct.id)
        )?.product

        if (firstUnselectedProduct) {
            // Get the reference to the product view and scroll to it.
            const {ref} = childProductRefs.current[firstUnselectedProduct.id]

            if (ref.scrollIntoView) {
                ref.scrollIntoView({
                    behavior: 'smooth',
                    block: 'end'
                })
            }

            return false
        }

        return true
    }, [product, childProductSelection])

    /**************** Product Set Handlers ****************/
    const handleProductSetAddToCart = () => {
        // Get all the selected products, and pass them to the addToCart handler which
        // accepts an array.
        const productSelectionValues = Object.values(childProductSelection)
        return handleAddToCart(productSelectionValues)
    }

    /**************** Product Bundle Handlers ****************/
    // Top level bundle does not have variants
    const handleProductBundleAddToCart = async (variant, selectedQuantity) => {
        try {
            const childProductSelections = Object.values(childProductSelection)

            const productItems = [
                {
                    productId: product.id,
                    price: product.price,
                    quantity: selectedQuantity,
                    c_itemAddLocation: INTERACTION_LOCATION.PDP,
                    // The add item endpoint in the shopper baskets API does not respect variant selections
                    // for bundle children, so we have to make a follow up call to update the basket
                    // with the chosen variant selections
                    bundledProductItems: childProductSelections.map((child) => {
                        return {
                            productId: child.variant.productId,
                            quantity: child.quantity,
                            c_itemAddLocation: INTERACTION_LOCATION.PDP
                        }
                    })
                }
            ]

            const res = await addItemToNewOrExistingBasket(productItems)

            // ATB Optimizely Web Experimentation
            trackAddToBagEvent({
                sku: productItems[0].productId,
                value: productItems[0].price,
                source: 'product-detail-bundle'
            })

            const bundleChildMasterIds = childProductSelections.map((child) => {
                return child.product.id
            })

            // since the returned data includes all products in basket
            // here we compare list of productIds in bundleProductItems of each productItem to filter out the
            // current bundle that was last added into cart
            const currentBundle = res.productItems.find((productItem) => {
                if (!productItem.bundledProductItems?.length) return
                const bundleChildIds = productItem.bundledProductItems?.map((item) => {
                    // seek out the bundle child that still uses masterId as product id
                    return item.productId
                })
                return bundleChildIds.every((id) => bundleChildMasterIds.includes(id))
            })

            const itemsToBeUpdated = getUpdateBundleChildArray(
                currentBundle,
                childProductSelections
            )

            if (itemsToBeUpdated.length) {
                // make a follow up call to update child variant selection for product bundle
                // since add item endpoint doesn't currently consider product bundle child variants
                await updateItemsInBasketMutation.mutateAsync({
                    method: 'PATCH',
                    parameters: {
                        basketId: res.basketId
                    },
                    body: itemsToBeUpdated
                })
            }

            // GTM Modification: Explicitly set c_gtm_index on product to ensure it takes
            // priority in sendItemCartEvent's resolution logic and isn't overridden by cached values
            if (initialGTMIndex >= 0) {
                productItems[0].c_gtm_index = initialGTMIndex
            }
            sendItemCartEvent(
                productItems[0],
                false,
                null,
                INTERACTION_LOCATION.PDP,
                res.currency
            )

            // Braze Modification - Send Braze Cart Event
            const brazeProduct = {
                ...productItems[0],
                imageGroups: childProductSelections[0]?.product?.imageGroups || [],
                c_color: childProductSelections[0]?.product?.c_color || '',
                c_relativeUrl: childProductSelections[0]?.product?.c_relativeUrl || '',
            }
            sendBrazeCartEvent(res, brazeProduct, 'add')

            return childProductSelections
        } catch (error) {
            showError(error)
        }
    }

    const initialGTMIndex = useMemo(() => {
        if (typeof window === 'undefined') {
            return -1
        }

        try {
            const productContext = JSON.parse(window.sessionStorage.getItem('plp-productContext'))
            const index = Number(productContext?.index)

            return Number.isFinite(index) && index >= 0 ? index : -1
        } catch {
            return -1
        }
    }, [effectiveProductId])

    const hasSentInitialViewItemRef = useRef(false)
    const lastViewItemProductIdRef = useRef(null)
    const lastBaseItemKeyRef = useRef(null)

    const sendPdpViewItemEvent = useCallback((payload) => {
        if (!payload) {
            return
        }

        const gtmPayload = initialGTMIndex >= 0 ? {...payload, index: initialGTMIndex} : payload

        if (lastViewItemProductIdRef.current !== effectiveProductId) {
            lastViewItemProductIdRef.current = effectiveProductId
            hasSentInitialViewItemRef.current = false
        }

        const interactionLocation = hasSentInitialViewItemRef.current
            ? INTERACTION_LOCATION.PDP
            : resolveViewItemInteractionLocation(location.pathname)

        hasSentInitialViewItemRef.current = true

        sendViewItemEvent(gtmPayload, interactionLocation)
        sendBrazeViewItemEvent(gtmPayload)
    }, [effectiveProductId, initialGTMIndex, location.pathname])

    /**************** Einstein ****************/
    useEffect(() => {
        setProduct(product)
        if (product && product.type.set) {
            sendCustomViewProduct(product)
            // GTM Modification
            if (gtmIds.indexOf(product.id) < 0) {
                setGTMIds((prev) => [...prev, product.id])
                sendPdpViewItemEvent(product)
            }
            const childrenProducts = product.setProducts
            childrenProducts.map((child) => {
                try {
                    sendCustomViewProduct(child)
                } catch (err) {
                    logger.error('Einstein sendViewProduct error', {
                        namespace: 'ProductDetail.useEffect',
                        additionalProperties: {error: err, child}
                    })
                }
                activeData.sendViewProduct(category, child, 'detail')
            })
        } else if (variant && product) {
            try {
                sendCustomViewProduct(product)
            } catch (err) {
                logger.error('Einstein sendViewProduct error', {
                    namespace: 'ProductDetail.useEffect',
                    additionalProperties: {error: err, product}
                })
            }
            activeData.sendViewProduct(category, product, 'detail')
        } else if (product) {
            try {
                sendCustomViewProduct(product)
            } catch (err) {
                logger.error('Einstein sendViewProduct error', {
                    namespace: 'ProductDetail.useEffect',
                    additionalProperties: {error: err, product}
                })
            }
            activeData.sendViewProduct(category, product, 'detail')
        }
    }, [product, sendPdpViewItemEvent])

    // Memoize just the expensive variant search
    const findVariantByParams = useMemo(() => {
        // Check if we have variationGroups (normal products) or variants (gift cards)
        if (product?.variationGroups?.length) {
            return (variationParams) => {
                return product.variationGroups.find((variant) => {
                    if (!variant.variationValues) return false
                    return Object.entries(variationParams).every(([key, value]) => {
                        return variant.variationValues[key] === value
                    })
                })
            }
        } else if (product?.variants?.length) {
            // For gift cards and similar products that only have variants
            return (variationParams) => {
                return product.variants.find((variant) => {
                    if (!variant.variationValues) return false
                    return Object.entries(variationParams).every(([key, value]) => {
                        return variant.variationValues[key] === value
                    })
                })
            }
        }
        return null
    }, [product?.variationGroups, product?.variants])

    // Used for GTM
    const gtmViewItemObject = useMemo(() => {
        if (isFindInStoreOpen || isPreviousProductData || !product) {
            return null
        }

        const colorName = product?.c_colorName || product?.representedProduct?.c_colorName
        const basePayload = {
            productId: getMatchingGroup(product) || product.id,
            productName: product.name,
            c_brand: product.brand,
            c_currency: product.currency,
            c_refinementColor: product?.c_refinementColor,
            ...(colorName && {c_colorName: colorName}),
            ...(initialGTMIndex >= 0 && {index: initialGTMIndex})
        }

        if (variant?.productId) {
            return {
                ...basePayload,
                price: variant.price,
                c_size: variant.variationValues?.size,
                representedProduct: {
                    id: variant.productId,
                    c_categories: product.c_categories,
                    c_refinementColor: product?.c_refinementColor,
                    ...(colorName && {c_colorName: colorName})
                }
            }
        }

        const qvSelections =
            typeof window !== 'undefined' ? getQVSelections(effectiveProductId) : null
        if (qvSelections?.size || qvSelections?.length) {
            return null
        }

        const sizeAttr = product.variationAttributes?.find((attr) => attr.id === 'size')
        const orderableSizes = sizeAttr?.values?.filter((value) => value.orderable) || []
        if (orderableSizes.length === 1) {
            return null
        }

        return {
            ...basePayload,
            price: product.price,
            representedProduct: {
                id: product.representedProduct?.id || product.productId || product.id,
                c_categories: product.c_categories,
                c_refinementColor: product?.c_refinementColor,
                ...(colorName && {c_colorName: colorName})
            }
        }
    }, [
        isFindInStoreOpen,
        isPreviousProductData,
        product,
        effectiveProductId,
        initialGTMIndex,
        variant?.productId,
        variant?.price,
        variant?.variationValues?.size,
        variant?.variationValues?.color
    ])

    useEffect(() => {
        if (!isFindInStoreOpen) {
            setIsVariantSelected(variant?.productId ? true : false)
        }
    }, [variant, isFindInStoreOpen])

    useEffect(() => {
        if (!gtmViewItemObject) return

        const key = [
            gtmViewItemObject.productId,
            gtmViewItemObject.representedProduct?.c_refinementColor ||
                gtmViewItemObject.c_refinementColor,
            gtmViewItemObject.c_colorName || gtmViewItemObject.representedProduct?.c_colorName,
            gtmViewItemObject.c_size || ''
        ].join('|')

        if (lastBaseItemKeyRef.current === key) return
        lastBaseItemKeyRef.current = key

        sendPdpViewItemEvent(gtmViewItemObject)
    }, [gtmViewItemObject, sendPdpViewItemEvent])

    // Helper to safely parse JSON strings
    const safeParse = (value) => {
        if (!value) return null
        if (typeof value === 'string') {
            try {
                return JSON.parse(value)
            } catch (e) {
                return null
            }
        }
        return typeof value === 'object' ? value : null
    }

    // Last-resort SSR fallback: the react-query prefetch can drop the product query, which
    // would emit an empty schema. seoMeta comes from Express middleware, which is unaffected.
    // `@id`/`url` are overridden because the backend builds them from URLUtils, which resolves
    // to the wrong brand host in headless.
    const injectedSchema = safeParse(seoMeta?.schemaMarkup)
    const injectedSchemaMarkup =
        injectedSchema && seoCanonicalUrl
            ? {...injectedSchema, url: seoCanonicalUrl, '@id': seoCanonicalUrl}
            : injectedSchema

    const selectedSchemaMarkup =
        activeSchemaMarkup || safeParse(product?.c_schemaMarkup) || injectedSchemaMarkup || null
    const selectedSchemaMarkupJson = useMemo(
        () =>
            !selectedSchemaMarkup
                ? '{}'
                : typeof selectedSchemaMarkup === 'string'
                ? selectedSchemaMarkup
                : JSON.stringify(selectedSchemaMarkup),
        [selectedSchemaMarkup]
    )

    /**************** Schema Markup ****************/

    /**************** Error Handling ****************/
    if (isProductError) {
        const errorStatus = productError?.response?.status
        if (errorStatus === 404) return <PageNotFound />
        // A thrown HTTPError hydrates into the app error boundary.
        return <ProductLoadError />
    }

    const BV_ENABLE_RR_C2022 = getSiteEnvVarValue(appConfig, 'BV_ENABLE_RR_C2022', siteId) || false

    return (
        <Box>
            {/* Above the fold section */}
            <Box
                className="sf-product-detail-page"
                layerStyle="PDPpage"
                data-testid="product-details-page"
                data-is-pdp="true"
                pb={{base: 12, lg: '64px'}}
            >
                <Seo title={seoTitle} canonicalUrl={seoCanonicalUrl}>
                    {lcpImagePreloadAttrs && (
                        <link
                            rel="preload"
                            fetchpriority="high"
                            as="image"
                            href={lcpImagePreloadAttrs.href}
                            {...(lcpImagePreloadAttrs.imagesrcset
                                ? {
                                      imagesrcset: lcpImagePreloadAttrs.imagesrcset,
                                      imagesizes: lcpImagePreloadAttrs.imagesizes
                                  }
                                : {})}
                        />
                    )}
                    {seoMetaTags.map((tag) => {
                        if (tag.id != 'title') {
                            // og:image is the product's own image (already brand-correct from the
                            // product data) — never rewrite it. Only og:site_name needs the brand
                            // name, which the SFCC OriginalRequestURL rule can't resolve in headless.
                            let content = tag.value
                            if (tag.id === 'og:site_name' && seoBrandId) {
                                content = seoBrandId.charAt(0).toUpperCase() + seoBrandId.slice(1)
                            }
                            return (
                                <meta
                                    key={tag.id}
                                    {...(tag.id.startsWith('og:')
                                        ? {property: tag.id, content}
                                        : {name: tag.id, content})}
                                />
                            )
                        }
                    })}
                    {seoCanonicalUrl && <link rel="canonical" href={seoCanonicalUrl} />}
                    {generateAlternateUrls(seoAlternateURLs)}
                    <script id="pdp-product-schema" type="application/ld+json">
                        {selectedSchemaMarkupJson || '{}'}
                    </script>
                </Seo>

                <Stack spacing={{base: 12, lg: 16}}>
                    {/* Guard set/bundle layouts with !isTransitioning so keepPreviousData doesn't paint
                        the previous product's layout before the new product resolves. */}
                    {isProductASet && !isTransitioning ? (
                        <ProductSetView
                            product={productWithPromotions}
                            category={primaryCategory?.parentCategoryTree || []}
                            isLoading={isProductLoading || isTransitioning}
                        />
                    ) : isProductABundle && !isTransitioning ? (
                        <Fragment>
                            <ProductView
                                basket={basket}
                                product={productWithPromotions}
                                category={primaryCategory?.parentCategoryTree || []}
                                addToCart={
                                    isProductASet
                                        ? handleProductSetAddToCart
                                        : handleProductBundleAddToCart
                                }
                                addToWishlist={handleAddToWishlist}
                                isInWishlist={isInWishlist}
                                isProductLoading={isProductLoading || isTransitioning}
                                isMasterProductChanging={isTransitioning}
                                isBasketLoading={isHydrated() && isBasketLoading}
                                isWishlistLoading={isHydrated() && isWishlistLoading}
                                isAvailabilityPending={isAvailabilityPending}
                                validateOrderability={handleChildProductValidation}
                                childProductOrderability={childProductOrderability}
                                setSelectedBundleQuantity={setSelectedBundleQuantity}
                                onFindInStoreOpen={onFindInStoreOpen}
                                onLongDescriptionOpen={onLongDescriptionOpen}
                                optimizelyContent={optimizelyData}
                                isOptimizelyLoading={isOptimizelyLoading}
                                onReviewsClick={scrollToReviews}
                                isPDP={true}
                            />

                            <hr />

                            {/* TODO: consider `childProduct.belongsToSet` */}
                            {
                                // Render the child products
                                comboProduct.childProducts.map(
                                    ({product: childProduct, quantity: childQuantity}) => (
                                        <Box key={childProduct.id} data-testid="child-product">
                                            <ProductView
                                                // Do not use an arrow function as we are manipulating the functions scope.
                                                ref={function (ref) {
                                                    // Assign the "set" scope of the ref, this is how we access the internal
                                                    // validation.
                                                    childProductRefs.current[childProduct.id] = {
                                                        ref,
                                                        validateOrderability:
                                                            this.validateOrderability
                                                    }
                                                }}
                                                basket={basket}
                                                product={childProduct}
                                                isProductPartOfSet={isProductASet}
                                                isProductPartOfBundle={isProductABundle}
                                                childOfBundleQuantity={childQuantity}
                                                selectedBundleParentQuantity={
                                                    selectedBundleQuantity
                                                }
                                                addToCart={
                                                    isProductASet
                                                        ? (variant, quantity) =>
                                                              handleAddToCart([
                                                                  {
                                                                      product: childProduct,
                                                                      variant,
                                                                      quantity
                                                                  }
                                                              ])
                                                        : null
                                                }
                                                addToWishlist={
                                                    isProductASet ? handleAddToWishlist : null
                                                }
                                                onVariantSelected={(product, variant, quantity) => {
                                                    if (quantity) {
                                                        setChildProductSelection(
                                                            (previousState) => ({
                                                                ...previousState,
                                                                [product.id]: {
                                                                    product,
                                                                    variant,
                                                                    quantity: isProductABundle
                                                                        ? childQuantity
                                                                        : quantity
                                                                }
                                                            })
                                                        )
                                                    } else {
                                                        const selections = {
                                                            ...childProductSelection
                                                        }
                                                        delete selections[product.id]
                                                        setChildProductSelection(selections)
                                                    }
                                                }}
                                                isProductLoading={
                                                    isProductLoading || isTransitioning
                                                }
                                                isMasterProductChanging={isTransitioning}
                                                isBasketLoading={isHydrated() && isBasketLoading}
                                                isWishlistLoading={
                                                    isHydrated() && isWishlistLoading
                                                }
                                                setChildProductOrderability={
                                                    setChildProductOrderability
                                                }
                                                isPDP={true}
                                            />
                                            <InformationAccordion
                                                key={`information-accordion-${product?.master?.masterId}`}
                                                product={childProduct}
                                                optimizelyContent={optimizelyData}
                                                isOptimizelyLoading={isOptimizelyLoading}
                                                pdpCmsContext={pdpCmsContext}
                                            />
                                            <Box display={['none', 'none', 'none', 'block']}>
                                                <hr />
                                            </Box>
                                        </Box>
                                    )
                                )
                            }
                        </Fragment>
                    ) : (
                        <Fragment>
                            <ProductView
                                basket={basket}
                                product={productWithPromotions}
                                category={primaryCategory?.parentCategoryTree || []}
                                addToCart={(variant, quantity, giftCardData) =>
                                    handleAddToCart([{product, variant, quantity}], giftCardData)
                                }
                                isInWishlist={isInWishlist}
                                addToWishlist={handleAddToWishlist}
                                isProductLoading={isProductLoading || isTransitioning}
                                isMasterProductChanging={isTransitioning}
                                isBasketLoading={isHydrated() && isBasketLoading}
                                isWishlistLoading={isHydrated() && isWishlistLoading}
                                isAvailabilityPending={isAvailabilityPending}
                                onFindInStoreOpen={onFindInStoreOpen}
                                onLongDescriptionOpen={onLongDescriptionOpen}
                                optimizelyContent={optimizelyData}
                                isOptimizelyLoading={isOptimizelyLoading}
                                onReviewsClick={scrollToReviews}
                                isPDP={true}
                            />
                        </Fragment>
                    )}
                </Stack>
                <GDIDynamicContentProvider
                    value={{
                        contentPageType: 'GDIPDPPage',
                        pdpProduct: productWithPromotions,
                        pdpProductSku: effectiveProductId,
                                                pdpLoyaltyPoints
                    }}
                >
                    {belowProductInfoBlocks.map((block, index) => (
                        <Box key={`below-product-info-${block?.ContentLink?.GuidValue ?? index}`}>
                            {renderLayoutsSwitch(block)}
                        </Box>
                    ))}
                </GDIDynamicContentProvider>
            </Box>
            {BV_ENABLE_RR_C2022 === 'true' && product && !isGiftCard && !isProductASet && (
                <Box ref={reviewsRef} padding={{base: '0 16px 48px', lg: '0 24px 64px'}}>
                    <PDPReviews key={'pdp-reviews'} product={productWithPromotions} />
                </Box>
            )}
            <LongDescriptionModal
                isOpen={isLongDescriptionOpen}
                onClose={onLongDescriptionClose}
                product={productWithPromotions}
                isVariantSelected={isVariantSelected}
                variantId={variant?.productId}
            />
            <FindInStoreModal
                isOpen={isFindInStoreOpen}
                onClose={onFindInStoreClose}
                isVariantSelected={isVariantSelected}
                setIsVariantSelected={setIsVariantSelected}
                findInStoreModalTitle={findInStoreModalTitle}
                setFindInStoreModalTitle={setFindInStoreModalTitle}
                variant={variant}
                productWithPromotions={productWithPromotions}
                productName={product?.name}
                variationAttributes={variationAttributes}
            />
        </Box>
    )
}

ProductDetail.getTemplateName = () => 'product-detail'

ProductDetail.propTypes = {
    /**
     * The current react router match object. (Provided internally)
     */
    match: PropTypes.object
}

export default ProductDetail
